/**
 * Plain text out of CMS HTML without handing the markup to the live document (rank 51).
 *
 * `document.createElement( 'div' ).innerHTML = body` adopts the markup into the page's document even though the
 * element is never attached: every `<img>` in an article body starts downloading and inline `on*` handlers run on
 * load or error. A DOMParser document has no browsing context, so nothing in it fetches or runs. Where DOMParser
 * does not exist (the sync worker), `stripHtml` does the same job with regular expressions.
 */
// Content that is never read as text
var NON_TEXT_SELECTOR = 'script, style, noscript';
var NON_TEXT_BLOCK = /<(script|style|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
var COMMENT = /<!--[\s\S]*?-->/g;
// Block-level tags end a word; inline tags (<b>, <a>, <span>) do not
var BLOCK_TAG = /<\/?(?:address|article|aside|blockquote|br|dd|div|dl|dt|figcaption|figure|footer|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\b[^>]*>/gi;
// A '<' followed by a space is text in HTML, so only a tag name or '!' opens a tag
var ANY_TAG = /<\/?[a-z!][^>]*>/gi;
var IMG_TAG = /<img\b/gi;
var ENTITY = /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi;
var NAMED_ENTITIES = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
    lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
    ndash: '–', mdash: '—', hellip: '…', bull: '•',
    copy: '©', reg: '®', trade: '™', deg: '°'
};
function decodeEntity(match, name) {
    var _a;
    if (name[0] !== '#')
        return (_a = NAMED_ENTITIES[name]) !== null && _a !== void 0 ? _a : match;
    var codePoint = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
    return codePoint > 0 && codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : match;
}
/** Decodes numeric character references and the common named entities; anything else is left as written. */
export function decodeHtmlEntities(text) {
    return text.includes('&') ? text.replace(ENTITY, decodeEntity) : text;
}
/**
 * The text of an HTML fragment by string surgery alone: no DOM, so it is safe in a worker and cheap enough to run
 * over a whole archive. Block tags become spaces so paragraphs do not run together.
 */
export function stripHtml(html) {
    var _a, _b;
    if (!html)
        return { text: '', imageCount: 0 };
    var withoutNonText = html.replace(COMMENT, '').replace(NON_TEXT_BLOCK, '');
    var imageCount = (_b = (_a = withoutNonText.match(IMG_TAG)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    var text = decodeHtmlEntities(withoutNonText.replace(BLOCK_TAG, ' ').replace(ANY_TAG, ''));
    return { text: text, imageCount: imageCount };
}
/**
 * The text and image count of an HTML fragment, parsed inertly with DOMParser (falls back to `stripHtml` where
 * DOMParser does not exist).
 */
export function summarizeHtml(html) {
    var _a, _b, _c, _d;
    if (!html)
        return { text: '', imageCount: 0 };
    if (typeof DOMParser === 'undefined')
        return stripHtml(html);
    // textContent runs '<p>one</p><p>two</p>' together as 'onetwo'; a space after each block tag keeps words apart
    var parsed = new DOMParser().parseFromString(html.replace(BLOCK_TAG, '$& '), 'text/html');
    parsed.querySelectorAll(NON_TEXT_SELECTOR).forEach(function (element) { return element.remove(); });
    return {
        text: (_b = (_a = parsed.body) === null || _a === void 0 ? void 0 : _a.textContent) !== null && _b !== void 0 ? _b : '',
        imageCount: (_d = (_c = parsed.body) === null || _c === void 0 ? void 0 : _c.querySelectorAll('img').length) !== null && _d !== void 0 ? _d : 0
    };
}
/** Runs of whitespace (including no-break spaces) folded to one space, trimmed. */
export function collapseWhitespace(text) {
    return text.replace(/\s+/g, ' ').trim();
}
/** The readable text of an HTML fragment on one line, parsed inertly. */
export function htmlToPlainText(html) {
    return collapseWhitespace(summarizeHtml(html).text);
}
/** 'N Minute Read': 1,500 visible characters a minute plus 12 seconds an image, never under a minute. */
export function readingTimeLabel(_a) {
    var text = _a.text, imageCount = _a.imageCount;
    var charNum = text.replace(/\s+/g, '').length;
    var timeForRound = 0.2;
    var time = Math.round(charNum / 1500 + imageCount * 0.2 + timeForRound);
    var parseTime = time < 1 ? 1 : time;
    return "".concat(parseTime, " Minute Read");
}
